// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { CategoryModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import Notify from "@rugby/App/Components/Notify";
import { shopByCategoryStyles } from "@rugby/App/Widgets/ShopByCategory/ShopByCategory.style";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
    selectedCategoryIds = "selectedCategoryIds",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.selectedCategoryIds]: string[];
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    categoriesDataView: getCategoriesDataView(state),
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = shopByCategoryStyles;

const ShopByCategoryWidget: React.FC<Props> = ({ fields, categoriesDataView, loadCategories }) => {
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";

    const [categories, setCategories] = React.useState<CategoryModel[]>([]);

    const filterTheCategoriesWithSelectedOnes = () => {
        const tempCategories: any = [];
        tempCategories.length = 0;
        if (
            categoriesDataView.value &&
            categoriesDataView.value.length &&
            fields.selectedCategoryIds &&
            fields.selectedCategoryIds.length
        ) {
            categoriesDataView.value.forEach((cat: any) => {
                fields.selectedCategoryIds.indexOf(cat.id) > -1 && tempCategories.push(cat);
            });
        }
        setCategories(tempCategories);
    };

    useEffect(() => {
        if (!categoriesDataView || !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    useEffect(() => {
        filterTheCategoriesWithSelectedOnes();
    }, [categoriesDataView, fields.selectedCategoryIds]);

    // An author dosen't select category
    if (!categories.length) {
        return isCmsEditing ? (
            <Notify
                messageType="danger"
                title="Shop By Category"
                body="you should select at least one category to show widget"
            />
        ) : null;
    }

    if (categoriesDataView.isLoading || !categoriesDataView) {
        return (
            <StyledWrapper {...styles.centeringWrapper} data-test-selector="shopByCategory_Loading">
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper data-test-selector="shopByCategory">
            {fields.title && <Typography {...styles.TitleText}>{fields.title}</Typography>}

            <GridContainer {...styles.container} gap={30} data-test-selector="shopByCategory_categories_list">
                {categories.map(category => (
                    <GridItem {...styles.categoryGridItem} key={`${category.id}`}>
                        <Link {...styles.categoryLink} href={category.path}>
                            <LazyImage
                                {...styles.categoryImage}
                                src={category.smallImagePath}
                                altText={category.imageAltText || category.shortDescription}
                            />
                            <StyledWrapper {...styles.categoryTextWrapper}>
                                <Typography {...styles.categoryText}>{category.shortDescription}</Typography>
                            </StyledWrapper>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    displayName: "Rugby - Shop By Category",
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "Shop By Category",
            fieldType: "General",
        },
        {
            name: fields.selectedCategoryIds,
            displayName: "Select Categories",
            editorTemplate: "CategoriesField",
            defaultValue: [],
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByCategoryWidget),
    definition,
};

export default widgetModule;
